'use client';

import { useCallback, useState, useEffect } from 'react';
import { createPublicClient, http, formatUnits } from 'viem';
import { base } from 'viem/chains';
import { useSession } from './useSession';
import { formatCurrency } from '~/lib/currency';

const USDC_ADDRESS = process.env.NEXT_PUBLIC_BASE_USDC_CONTRACT!;

interface UsdcBalance {
  balance: string;
  formatted: string;
  hasBalance: boolean;
}

export function useUsdcBalance() {
  const { session } = useSession();
  const [balance, setBalance] = useState<UsdcBalance | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBalance = useCallback(async () => {
    if (!session?.user?.address) {
      setError('No wallet address available');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const rpcUrl = process.env.NEXT_PUBLIC_BASE_RPC_URL || 'https://mainnet.base.org';
      
      if (!process.env.NEXT_PUBLIC_BASE_RPC_URL) {
        console.warn('NEXT_PUBLIC_BASE_RPC_URL not configured, using fallback');
      }
      
      const client = createPublicClient({
        chain: base,
        transport: http(rpcUrl),
      });

      const balanceResult = await client.readContract({
        address: USDC_ADDRESS as `0x${string}`,
        abi: [
          {
            name: 'balanceOf',
            type: 'function',
            stateMutability: 'view',
            inputs: [{ name: 'account', type: 'address' }],
            outputs: [{ name: '', type: 'uint256' }],
          },
        ],
        functionName: 'balanceOf',
        args: [session.user.address as `0x${string}`],
      });

      const macroUnits = formatUnits(balanceResult as bigint, 6);
      const hasBalance = parseFloat(macroUnits) > 0;
      const formattedBalance = formatCurrency(parseFloat(macroUnits), 'USD');

      setBalance({
        balance: balanceResult.toString(),
        formatted: formattedBalance,
        hasBalance,
      });
    } catch (err: any) {
      console.error('Failed to load USDC balance:', err);
      setError(err.message || 'Failed to load USDC balance');
    } finally {
      setIsLoading(false);
    }
  }, [session?.user?.address]);

  // Load balance when session changes
  useEffect(() => {
    if (session?.user?.address) {
      loadBalance();
    }
  }, [session?.user?.address, loadBalance]);

  const refetch = useCallback(() => {
    loadBalance();
  }, [loadBalance]);

  return {
    balance,
    isLoading,
    error,
    refetch,
  };
}
